import React, { useState } from "react";
import { Layout, Dropdown, Grid } from "antd";
import {
  LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined, CloseOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  HiOutlineArrowsRightLeft,
  HiOutlineDocumentArrowDown,
  HiOutlineDocumentDuplicate,
  HiOutlineDocumentMagnifyingGlass,
  HiOutlineDocumentPlus,
  HiOutlineDocumentText,
  HiOutlineHome,
  HiOutlineKey,
  HiOutlineLockClosed,
  HiOutlinePhoto,
  HiOutlinePresentationChartBar,
  HiOutlineQueueList,
  HiOutlineScissors,
  HiOutlineSquares2X2,
  HiOutlineTableCells,
} from "react-icons/hi2";
import "./Dashboard.css";

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const NAV_GROUPS = [
  {
    key: "overview",
    items: [
      {
        key: "home",
        label: "Overview",
        icon: HiOutlineHome,
        path: "/dashboard/home",
      },
    ],
  },
  {
    key: "convert",
    label: "Convert to PDF",
    collapsible: true,
    items: [
      { key: "word2pdf",  label: "Word to PDF",       icon: HiOutlineDocumentText,         path: "/dashboard/word2pdf",  color: "#2b5ce6" },
      { key: "excel2pdf", label: "Excel to PDF",      icon: HiOutlineTableCells,           path: "/dashboard/excel2pdf", color: "#1a7a43" },
      { key: "ppt2pdf",   label: "PowerPoint to PDF", icon: HiOutlinePresentationChartBar, path: "/dashboard/ppt2pdf",   color: "#c84b1e" },
      { key: "image2pdf", label: "Image to PDF",      icon: HiOutlinePhoto,                path: "/dashboard/image2pdf", color: "#7b3fe4" },
    ],
  },
  {
    key: "advanced",
    label: "Advanced PDF",
    collapsible: true,
    items: [
      { key: "merge",      label: "Merge PDF",       icon: HiOutlineDocumentDuplicate,      path: "/dashboard/merge",      color: "#6366f1" },
      { key: "split",      label: "Split PDF",       icon: HiOutlineScissors,               path: "/dashboard/split",      color: "#0891b2" },
      { key: "rearrange",  label: "Rearrange PDF",   icon: HiOutlineArrowsRightLeft,        path: "/dashboard/rearrange",  color: "#0891b2" },
      { key: "compress",   label: "Compress PDF",    icon: HiOutlineSquares2X2,             path: "/dashboard/compress",   color: "#1a7a43" },
      { key: "ocr",        label: "OCR PDF",         icon: HiOutlineDocumentMagnifyingGlass,path: "/dashboard/ocr",        color: "#7b3fe4" },
      { key: "esign",      label: "E-sign PDF",      icon: HiOutlineDocumentPlus,           path: "/dashboard/esign",      color: "#c84b1e" },
      { key: "addpass",    label: "Add Password",    icon: HiOutlineLockClosed,             path: "/dashboard/addpass",    color: "#e5322d" },
      { key: "removepass", label: "Remove Password", icon: HiOutlineKey,                    path: "/dashboard/removepass", color: "#e5322d" },
    ],
  },
  {
    key: "qr",
    label: "QR Codes",
    collapsible: true,
    items: [
      { key: "qr-link", label: "Generate QR", icon: HiOutlineQueueList, path: "/dashboard/qr-link", color: "#0891b2" },
    ],
  },
  {
    key: "files",
    label: "My Files",
    collapsible: false,
    items: [
      { key: "pdf-list", label: "Converted PDFs", icon: HiOutlineDocumentArrowDown, path: "/dashboard/pdf-list", color: "#e5322d" },
      { key: "qr-list",  label: "My QR Codes",    icon: HiOutlineQueueList,         path: "/dashboard/qr-list",  color: "#6366f1" },
    ],
  },
];

// Flat list for active key lookup
const ALL_NAV = NAV_GROUPS.flatMap((g) => g.items);
const QR_CREATE_PATHS = [
  "/dashboard/qr-link",
  "/dashboard/qr-text",
  "/dashboard/qr-location",
  "/dashboard/qr-brand",
  "/dashboard/qr-social",
  "/dashboard/qr-file",
  "/dashboard/qr-vcard",
  "/dashboard/qr-product",
  "/dashboard/qr-message",
];

const Dashboard = () => {
  const navigate   = useNavigate();
  const location   = useLocation();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const screens    = useBreakpoint();
  const isMobile   = !screens.md;

  const [collapsed,   setCollapsed]   = useState(false);
  const [mobileOpen,  setMobileOpen]  = useState(false);
  // Track which collapsible groups are open
  const [openGroups,  setOpenGroups]  = useState({ convert: true, advanced: true, qr: true, files: true });

  const activeKey = QR_CREATE_PATHS.includes(location.pathname)
    ? "qr-link"
    : ALL_NAV.find((n) => location.pathname === n.path)?.key ?? "home";
  const activeLabel = ALL_NAV.find((n) => n.key === activeKey)?.label ?? "Overview";

  const handleLogout = () => { localStorage.clear(); navigate("/"); };

  const toggleGroup = (key) => setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const go = (path) => { navigate(path); if (isMobile) setMobileOpen(false); };

  const userInitial = (user.name || "U").charAt(0).toUpperCase();

  const userMenuItems = [
    {
      key: "profile",
      label: (
        <div style={{ padding: "4px 0" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1a1a2e" }}>{user.name || "User"}</div>
          <div style={{ fontSize: 12, color: "#9ca3af" }}>{user.username || user.email || ""}</div>
        </div>
      ),
      disabled: true,
    },
    { type: "divider" },
    {
      key: "logout",
      icon: <LogoutOutlined style={{ color: "#e5322d" }} />,
      label: <span style={{ color: "#e5322d", fontWeight: 600 }}>Sign out</span>,
      onClick: handleLogout,
    },
  ];

  const renderNavIcon = (Icon) => <Icon className="db-nav-icon" aria-hidden="true" />;

  // ── Sidebar content ──────────────────────────────────────────────
  const siderContent = (
    <div className="db-sider-content">

      {/* Logo */}
      <div className="db-logo" onClick={() => navigate("/")}>
        <img src={require("../../assets/logo.png")} alt="Bonoxi" className="db-logo-img" />
        {!collapsed && <span className="db-logo-pill">My Account</span>}
      </div>

      {/* Nav */}
      <nav className="db-nav">
        {NAV_GROUPS.map((group) => (
          <div key={group.key} className="db-nav-group-wrap">

            {/* Overview — no header */}
            {group.key === "overview" && group.items.map((item) => (
              <button
                key={item.key}
                className={`db-nav-item ${activeKey === item.key ? "active" : ""}`}
                onClick={() => go(item.path)}
                title={collapsed ? item.label : ""}
              >
                <span className="db-nav-icon-wrap">{renderNavIcon(item.icon)}</span>
                {!collapsed && <span className="db-nav-label">{item.label}</span>}
              </button>
            ))}

            {/* Groups with header */}
            {group.key !== "overview" && (
              <>
                {!collapsed && (
                  <button
                    className={`db-group-header ${group.collapsible ? "clickable" : ""}`}
                    onClick={() => group.collapsible && toggleGroup(group.key)}
                  >
                    <span className="db-group-label">{group.label}</span>
                    {group.collapsible && (
                      <span className={`db-group-chevron ${openGroups[group.key] ? "open" : ""}`}>
                        ›
                      </span>
                    )}
                  </button>
                )}
                {collapsed && <div className="db-nav-divider" />}

                {(collapsed || !group.collapsible || openGroups[group.key]) && (
                  <div className={`db-group-items ${!collapsed && group.collapsible && !openGroups[group.key] ? "hidden" : ""}`}>
                    {group.items.map((item) => (
                      <button
                        key={item.key}
                        className={`db-nav-item ${activeKey === item.key ? "active" : ""}`}
                        onClick={() => go(item.path)}
                        title={collapsed ? item.label : ""}
                        style={{ "--item-color": item.color || "#e5322d" }}
                      >
                        <span className="db-nav-icon-wrap">{renderNavIcon(item.icon)}</span>
                        {!collapsed && <span className="db-nav-label">{item.label}</span>}
                        {!collapsed && activeKey === item.key && (
                          <span className="db-nav-active-dot" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </nav>

      {/* User card */}
      <div className={`db-user-card ${collapsed ? "collapsed" : ""}`}>
        {collapsed ? (
          <div className="db-user-avatar-only" onClick={handleLogout} title="Sign out">
            <div className="db-avatar">{userInitial}</div>
          </div>
        ) : (
          <>
            <div className="db-avatar">{userInitial}</div>
            <div className="db-user-info">
              <div className="db-user-name">{user.name || "User"}</div>
              <div className="db-user-plan">Free Plan</div>
            </div>
            <button className="db-logout-btn" onClick={handleLogout} title="Sign out">
              <LogoutOutlined />
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Layout className="db-root">

      {/* Desktop sidebar */}
      {!isMobile && (
        <Sider
          collapsed={collapsed}
          collapsedWidth={64}
          width={256}
          theme="light"
          className="db-sider"
        >
          {siderContent}
        </Sider>
      )}

      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div className="db-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile drawer */}
      {isMobile && (
        <div className={`db-mobile-drawer ${mobileOpen ? "open" : ""}`}>
          <button className="db-drawer-close" onClick={() => setMobileOpen(false)}>
            <CloseOutlined />
          </button>
          {siderContent}
        </div>
      )}

      <Layout>
        {/* Top header */}
        <Header className="db-header">
          <div className="db-header-left">
            <button
              className="db-toggle"
              onClick={() => isMobile ? setMobileOpen(!mobileOpen) : setCollapsed(!collapsed)}
            >
              {isMobile
                ? <MenuUnfoldOutlined />
                : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
              }
            </button>
            <div className="db-breadcrumb">
              <span className="db-breadcrumb-parent" onClick={() => navigate("/dashboard/home")}>Dashboard</span>
              {activeKey !== "home" && (
                <>
                  <span className="db-breadcrumb-sep">›</span>
                  <span className="db-breadcrumb-current">{activeLabel}</span>
                </>
              )}
            </div>
          </div>

          <div className="db-header-right">
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={["click"]}>
              <div className="db-header-user">
                <div className="db-avatar sm">{userInitial}</div>
                {!isMobile && (
                  <div className="db-header-user-info">
                    <span className="db-header-name">{user.name || "User"}</span>
                    <span className="db-header-plan">Free Plan</span>
                  </div>
                )}
                <span className="db-header-chevron">›</span>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="db-content">
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default Dashboard;
